import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { FileText, Network, ShieldCheck, Clock, Lock, ExternalLink } from "lucide-react";
import { BuyerIndirectClaimsPanel } from "../components/BuyerIndirectClaimsPanel";
import { AccountShell, StatusPill, FileUpload } from "../components/AccountShell";
import { Input, Textarea, SubmitButton } from "../components/RegisterLayout";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { useSecurityPulse } from "../hooks/useSecurityPulse";
import { formatINR } from "../lib/format";

const EMPTY = {
  order_ref: "", order_date: "", order_value: "", material: "", quantity: "",
  rate_pct: "", amount_claimed: "", payout_details: "", remarks: "",
  end_customer: "", deal_role: "", document_url: "",
  sponsor_request_note: "", sponsor_ack: false,
};

const LEVEL2 = new Set(["introducer", "custodian", "guarantor"]);

/**
 * SettlementWorksheetButton — Buyer-facing gate for the OFFICIAL FINANCIAL
 * WORKOUT PDF. Polls the release-status endpoint (which returns 403
 * `{code: "not_released"}` until the admin releases the PDF from the
 * Settlement Desk). When still locked, shows a padlocked disabled button
 * with a "Awaiting Vinci Executive Approval" label; once released, becomes
 * a live download.
 */
function SettlementWorksheetButton({ api, claimId }) {
  const [state, setState] = useState({ loading: true, released: false });
  const check = useCallback(async () => {
    try {
      // Attempt an inexpensive HEAD-equivalent — a GET but only read the
      // failure detail; if the endpoint returns a PDF we treat it as
      // released.
      const r = await api.get(`/claims/${claimId}/settlement-worksheet`, {
        responseType: "blob", validateStatus: (s) => s === 200 || s === 403,
      });
      if (r.status === 200) {
        setState({ loading: false, released: true, blob: r.data });
      } else {
        // 403 → parse the detail (blob → text)
        let detail = "not_released";
        try { detail = JSON.parse(await r.data.text()).detail?.code || "not_released"; } catch { /* ignore */ }
        setState({ loading: false, released: false, code: detail });
      }
    } catch (e) {
      setState({ loading: false, released: false, code: "error" });
    }
  }, [api, claimId]);
  useEffect(() => { check(); }, [check]);

  const download = () => {
    if (!state.blob) return;
    const u = URL.createObjectURL(state.blob);
    const a = document.createElement("a");
    a.href = u; a.download = `Vinci-Financial-Workout-${claimId.slice(0, 8).toUpperCase()}.pdf`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
  };

  if (state.loading) {
    return (
      <span className="rounded-full border border-white/15 text-[#8A8A82] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5" data-testid={`worksheet-loading-${claimId}`}>
        <Clock size={12} /> Checking release status…
      </span>
    );
  }
  if (!state.released) {
    return (
      <span className="rounded-full border border-[#8B1E1E]/40 bg-[#8B1E1E]/[0.06] text-[#8B1E1E] px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-1.5 cursor-not-allowed"
            title="The Official Financial Workout has not yet been released to you by the Vinci Executive Command."
            data-testid={`worksheet-locked-${claimId}`}>
        <Lock size={12} /> Awaiting Executive Approval
      </span>
    );
  }
  return (
    <button data-testid={`worksheet-${claimId}`} onClick={download}
            className="rounded-full border border-[#D8B23E]/50 bg-[#D8B23E]/10 text-[#D8B23E] hover:bg-[#D8B23E]/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-1.5">
      <ExternalLink size={12} /> Financial Workout · Released
    </button>
  );
}

const KINDS = [
  { key: "direct", label: "Direct Orders", Icon: FileText, blurb: "Claim commission & incentives on orders you placed directly with Vinci Inc." },
  { key: "indirect", label: "Indirect Orders", Icon: Network, blurb: "Claim on orders you facilitated for an end-customer or through a sub-channel." },
];

export default function BuyerClaims() {
  const { user, api } = useAuth();
  const pulse = useSecurityPulse(api);
  const [kind, setKind] = useState("direct");
  const [form, setForm] = useState(EMPTY);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claims, setClaims] = useState([]);
  const [lastClaim, setLastClaim] = useState(null);

  const load = useCallback(async () => {
    try { setClaims((await api.get("/claims/mine")).data); } catch { /* ignore */ }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const downloadAck = async (id) => {
    try {
      const res = await api.get(`/claims/${id}/acknowledgement`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Vinci-Claim-Ack-${id.slice(0, 8).toUpperCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download acknowledgement."); }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      if (name === "order_value" || name === "rate_pct") {
        const ov = parseFloat(name === "order_value" ? value : next.order_value) || 0;
        const rt = parseFloat(name === "rate_pct" ? value : next.rate_pct) || 0;
        if (ov && rt) next.amount_claimed = Math.round((ov * rt) / 100);
      }
      return next;
    });
  };

  if (user === null) return <div className="min-h-screen bg-[#070707]" />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.account_type !== "buyer") return <Navigate to="/account" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const isDependent = user?.parent_id && LEVEL2.has(user?.parent_category);
    if (isDependent) {
      if (!(form.sponsor_request_note || "").trim()) {
        toast.error("Formal request to your sponsor is required."); return;
      }
      if (!form.sponsor_ack) {
        toast.error("You must acknowledge that your sponsor's approval is being sought."); return;
      }
    }
    setLoading(true);
    try {
      // ── Vinci Security Pulse · Commission Claim gate ──────────────
      let pulseToken;
      try { pulseToken = await pulse.request("commission_claim", null); }
      catch { setLoading(false); return; }
      const { data } = await api.post("/claims", {
        kind,
        order_ref: form.order_ref,
        order_date: form.order_date,
        order_value: parseFloat(form.order_value) || 0,
        material: form.material,
        quantity: form.quantity,
        rate_pct: parseFloat(form.rate_pct) || 0,
        amount_claimed: parseFloat(form.amount_claimed) || 0,
        payout_details: form.payout_details,
        remarks: form.remarks,
        document_url: form.document_url,
        end_customer: kind === "indirect" ? form.end_customer : "",
        deal_role: kind === "indirect" ? form.deal_role : "",
        sponsor_request_note: isDependent ? form.sponsor_request_note : "",
        sponsor_ack: !!(isDependent && form.sponsor_ack),
      }, { headers: { "X-Security-Pulse": pulseToken } });
      if (data.status === "auto_rejected") {
        toast.error(`Auto-rejected · ${data.rejection_reason || "sponsor consent gate failed"}`);
      } else if (data.status === "pending_sponsor_approval") {
        toast.success(`Claim routed to ${user?.parent_business_name || "your sponsor"} · 48h SLA started`);
      } else {
        toast.success("Claim submitted. Download your acknowledgement below.");
      }
      setLastClaim(data);
      setForm(EMPTY); setDoc(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit claim.");
    } finally { setLoading(false); }
  };

  return (
    <AccountShell
      kicker="/ Buyer · Commission & Incentives"
      title="Claim your commission"
      subtitle="Submit a claim to receive the commission & incentives due to you. Choose the claim type below, complete the form, and track its status in real time."
    >
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="grid sm:grid-cols-2 gap-4">
        {KINDS.map((k) => {
          const selected = kind === k.key;
          return (
            <button
              key={k.key}
              type="button"
              data-testid={`claim-kind-${k.key}`}
              onClick={() => setKind(k.key)}
              className={`group text-left rounded-sm border p-6 transition-all duration-300 ${selected ? "border-[#B87333]/60 bg-[#B87333]/[0.06]" : "border-white/10 hover:border-white/25"}`}
            >
              <span className="grid place-items-center w-11 h-11 rounded-full text-black" style={{ background: selected ? "#B87333" : "#3a3a35" }}>
                <k.Icon size={18} />
              </span>
              <div className="font-serif text-xl mt-5">{k.label}</div>
              <p className="font-body text-xs text-[#8A8A82] mt-2 leading-relaxed">{k.blurb}</p>
            </button>
          );
        })}
      </div>

      <AnimatePresence mode="wait">
        <motion.form
          key={kind}
          data-testid="claim-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
          className="mt-12 pt-10 border-t border-white/10"
        >
          <h3 className="font-serif text-2xl md:text-3xl mb-8">
            {kind === "direct" ? "Direct order claim" : "Indirect order claim"}
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            <Input label="Order / Invoice reference" testid="claim-order-ref" name="order_ref" required value={form.order_ref} onChange={onChange} />
            <Input label="Order date" testid="claim-order-date" name="order_date" type="date" value={form.order_date} onChange={onChange} />
            <Input label="Order value (₹)" testid="claim-order-value" name="order_value" type="number" min="0" required value={form.order_value} onChange={onChange} />
            <Input label="Material" testid="claim-material" name="material" required value={form.material} onChange={onChange} />
            <Input label="Quantity" testid="claim-quantity" name="quantity" placeholder="e.g. 500 MT" value={form.quantity} onChange={onChange} />
            <Input label="Commission / incentive rate (%)" testid="claim-rate" name="rate_pct" type="number" step="0.01" min="0" required value={form.rate_pct} onChange={onChange} />
            <Input label="Amount claimed (₹)" testid="claim-amount" name="amount_claimed" type="number" min="0" required value={form.amount_claimed} onChange={onChange} />
            <div />
            {kind === "indirect" && (
              <>
                <Input label="End-customer / project" testid="claim-end-customer" name="end_customer" required value={form.end_customer} onChange={onChange} />
                <Input label="Your role in the deal" testid="claim-deal-role" name="deal_role" required value={form.deal_role} onChange={onChange} />
              </>
            )}
            <Textarea label="Bank / UPI payout details" testid="claim-payout" name="payout_details" required rows={2} value={form.payout_details} onChange={onChange} className="md:col-span-2" />
            <Textarea label="Remarks" testid="claim-remarks" name="remarks" rows={2} value={form.remarks} onChange={onChange} className="md:col-span-2" />

            {user?.parent_id && LEVEL2.has(user?.parent_category) && (
              <div className="md:col-span-2 mt-4 border border-[#D8B23E]/40 rounded-sm bg-gradient-to-r from-[#D8B23E]/[0.08] via-[#B87333]/[0.05] to-transparent p-5"
                   data-testid="sponsor-consent-block">
                <div className="flex items-center gap-2 mb-2">
                  <ShieldCheck size={16} className="text-[#D8B23E]"/>
                  <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Critical · Sponsor consent</div>
                </div>
                <div className="font-serif text-xl text-[#F5F5F0] leading-snug">
                  Formal request to <strong>{user?.parent_business_name || `your ${user?.parent_category}`}</strong>
                </div>
                <p className="font-body text-[13px] text-[#B8B8B0] mt-2 max-w-3xl leading-relaxed">
                  Every claim you raise <strong>must</strong> be co-signed by your <strong>{user?.parent_category}</strong>. Compose a brief written request describing why this claim is due. It is sent to their inbox alongside the claim. Approval SLA is <strong>48 hours</strong>. If they don't respond in time, the system auto-rejects and emails you a rejection acknowledgement PDF.
                </p>
                <Textarea label="Written request to your sponsor · required" testid="claim-sponsor-note"
                          name="sponsor_request_note" rows={4} required
                          value={form.sponsor_request_note} onChange={onChange}
                          className="mt-4"/>
                <label className="mt-3 flex items-start gap-3 cursor-pointer" data-testid="claim-sponsor-ack-label">
                  <input type="checkbox" data-testid="claim-sponsor-ack"
                         checked={form.sponsor_ack}
                         onChange={(e) => setForm(f => ({ ...f, sponsor_ack: e.target.checked }))}
                         className="mt-1 accent-[#D8B23E]"/>
                  <span className="font-body text-[13px] text-[#F5F5F0]">
                    I confirm my sponsor's approval is being formally requested. I understand that without their sign-off within <strong>48 hours</strong>, this claim will be <strong>auto-rejected</strong> by the Vinci compliance engine.
                  </span>
                </label>
                <div className="mt-3 inline-flex items-center gap-2 text-[11px] font-grotesk uppercase tracking-[0.18em] text-[#8A8A82]">
                  <Clock size={12}/> 48-hour SLA · Zoho-signed decision · PDF acknowledgement on outcome
                </div>
              </div>
            )}

            <div className="md:col-span-2">
              <FileUpload
                testid="claim-file"
                doc={doc}
                onUploaded={(d, err) => {
                  if (err) { toast.error(err); return; }
                  setDoc(d); setForm((f) => ({ ...f, document_url: d.url }));
                  toast.success("Document attached.");
                }}
              />
            </div>
          </div>
          <div className="mt-10">
            <SubmitButton testid="claim-submit" type="submit" disabled={loading}>
              {loading ? "Submitting…" : "Submit claim"}
            </SubmitButton>
          </div>
        </motion.form>
      </AnimatePresence>

      {lastClaim && (
        <motion.div
          data-testid="claim-ack-banner"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-sm border border-[#2FB999]/40 bg-[#2FB999]/[0.07] px-6 py-5"
        >
          <div>
            <div className="font-serif text-xl text-[#2FB999]">Claim {lastClaim.order_ref} received</div>
            <p className="font-body text-sm text-[#C9C9C2] mt-1">Keep the acknowledgement PDF for your records. Our trade desk will review and update the status.</p>
          </div>
          <button data-testid="ack-download-latest" onClick={() => downloadAck(lastClaim.id)} className="rounded-full bg-[#2FB999] text-black px-5 py-2.5 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:opacity-90 transition-opacity">Download acknowledgement</button>
        </motion.div>
      )}

      <div className="mt-20">
        <h2 className="font-serif text-2xl md:text-3xl mb-6">
          Your claims <span className="text-[#8A8A82] text-lg">({claims.length})</span>
        </h2>
        {claims.length === 0 ? (
          <p className="font-body text-[#8A8A82]" data-testid="no-claims">No claims submitted yet.</p>
        ) : (
          <div className="space-y-4" data-testid="claims-list">
            {claims.map((c) => (
              <div key={c.id} data-testid="claim-row" className="border border-white/10 rounded-sm p-6 hover:border-white/20 transition-colors">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="font-grotesk text-[10px] uppercase tracking-[0.2em] text-[#B87333] border border-[#B87333]/40 rounded-full px-3 py-1">{c.kind}</span>
                    <span className="font-serif text-lg">{c.order_ref}</span>
                    <StatusPill status={c.status} testid={`claim-status-${c.id}`} />
                  </div>
                  <span className="font-body text-xs text-[#8A8A82]">{new Date(c.created_at).toLocaleDateString()}</span>
                </div>
                <div className="font-body text-sm text-[#8A8A82] mt-3">
                  {c.material} · {c.quantity || "—"} · Rate {c.rate_pct}% · Claimed <span className="text-[#2FB999]">{formatINR(c.amount_claimed)}</span>
                </div>
                <div className="mt-4 pt-3 border-t border-white/10 flex gap-2 flex-wrap items-center">
                  <button data-testid={`ack-${c.id}`} onClick={() => downloadAck(c.id)} className="rounded-full border border-white/20 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.2em] hover:border-[#B87333] transition-colors">Download acknowledgement</button>
                  <SettlementWorksheetButton api={api} claimId={c.id} />
                </div>
              </div>
            ))}
          </div>
        )}

        <BuyerIndirectClaimsPanel api={api} />
      </div>
      {pulse.modal}
    </AccountShell>
  );
}
